import type { PointerEvent, ReactElement, Ref } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { useEditorContext } from '../editor_context';

export const tiptapButtonClassName = 'button-ghost-icon';

/** Stops toolbar controls from taking focus before click (selection / mobile keyboard). */
export function tiptapToolbarPointerDown(e: PointerEvent) {
   if (e.button === 0) {
      e.preventDefault();
   }
}

type TiptapButtonProps = {
   ref?: Ref<HTMLButtonElement>;
   onClick: () => void;
   isActive?: boolean;
   isDisabled?: boolean;
   icon: ReactElement<any>;
   title: string;
   shortcut?: string;
   overrideShortcut?: string;
   className?: string;
   'aria-label'?: string;
};

export function TiptapButton({
   ref,
   shortcut,
   overrideShortcut,
   title,
   onClick,
   isDisabled,
   isActive,
   icon,
   className,
   'aria-label': ariaLabel,
}: TiptapButtonProps) {
   // Label logic originally from prosemirror-example-setup/menu/menu.js
   const hasShortcut = Boolean(shortcut || overrideShortcut);
   const modKey = navigator.userAgent.indexOf('Mac') > 0 ? '⌘' : 'ctrl+';
   const shortcutText = overrideShortcut ?? `${modKey}${shortcut}`;
   const label = hasShortcut ? `${title} (${shortcutText})` : title;

   const { editor } = useEditorContext();

   const buttonClass = cn(tiptapButtonClassName, className);

   if (isDisabled) {
      return (
         <button
            type="button"
            ref={ref}
            disabled
            aria-label={ariaLabel ?? title}
            data-active={isActive ? 'true' : undefined}
            className={buttonClass}
         >
            {icon}
         </button>
      );
   }

   return (
      <Tooltip>
         <TooltipTrigger asChild>
            <button
               type="button"
               ref={ref}
               aria-label={ariaLabel ?? title}
               data-active={isActive ? 'true' : undefined}
               className={buttonClass}
               onPointerDown={tiptapToolbarPointerDown}
               onClick={e => {
                  e.preventDefault();
                  editor.view.focus();
                  onClick();
               }}
            >
               {icon}
            </button>
         </TooltipTrigger>
         <TooltipContent side="top" align="start">
            {label}
         </TooltipContent>
      </Tooltip>
   );
}
